import React, { memo, useRef, useEffect, useMemo, useState, useCallback } from 'react'
import { Tabs } from 'antd'
import type { TabsProps } from 'antd'
import { EmployeeViewForm } from '../employee-view-form/employee-view-form'
import { EmployeeForms, EmployeeFormsRef } from '../employee-forms/employee-forms'
import { TabTimeline } from '../tabs/tab-timeline'
import { TabFiles } from '../tabs/tab-files'
import { EmployeeFormData } from '../../types/types'
import EntityRelations from '../../../../../components/entity-relations'

interface EmployeeCardContentProps {
  id: string | number
  formData: EmployeeFormData | null
  loading: boolean
  isEditMode: boolean
  onSaveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

export const EmployeeCardContent: React.FC<EmployeeCardContentProps> = memo(
  (props: EmployeeCardContentProps) => {
    const { id, formData, loading, isEditMode, onSaveRef } = props
    const formsRef = useRef<EmployeeFormsRef>(null)
    const [activeKey, setActiveKey] = useState<string>('data')

    // Передаем метод save в родительский компонент через ref
    useEffect(() => {
      if (onSaveRef && formsRef.current) {
        onSaveRef.current = formsRef.current.save
      } else if (onSaveRef) {
        onSaveRef.current = null
      }
    }, [onSaveRef, isEditMode])

    const handleChange = useCallback((key: string) => {
      setActiveKey(key)
    }, [])

    // В режиме просмотра показываем табы
    const tabItems = useMemo<TabsProps['items']>(() => {
      if (isEditMode) {
        // В режиме редактирования табы не нужны, возвращаем пустой массив
        return []
      }

      const items: TabsProps['items'] = [
        {
          key: 'data',
          label: 'Данные сотрудника',
          children: <EmployeeViewForm formData={formData} loading={loading} />,
        },
      ]

      if (id !== 'new') {
        items.push(
          ...[
            {
              key: 'timeline',
              label: 'Лента событий',
              children: <TabTimeline employeeId={id} loading={loading} />,
            },
            {
              key: 'files',
              label: 'Прикрепленные файлы',
              children: <TabFiles id={id} />,
            },
            {
              key: 'relations',
              label: 'Связи',
              children: <EntityRelations entityType="employees" entityId={id} />,
            },
          ],
        )
      }

      return items
    }, [id, formData, loading, isEditMode])

    // В режиме редактирования показываем только форму без табов
    if (isEditMode) {
      return (
        <EmployeeForms ref={formsRef} id={id} formData={formData} loading={loading} error={null} />
      )
    }

    return <Tabs activeKey={activeKey} onChange={handleChange} items={tabItems} />
  },
)

EmployeeCardContent.displayName = 'EmployeeCardContent'
